import type { ReviewStatus } from "@common/client"
import {
  ReviewStatusSchema,
  SortDirectionSchema,
} from "@common/client/schemas.gen"
import { z } from "zod"
import type { Display } from "@/components/layout/display-options"
import type { ListSort } from "@/components/layout/list-table"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  sortSchema,
  type TaskSort,
  withFixed,
} from "./columns"

export const REVIEW_STATUSES: ReviewStatus[] = [
  "approved",
  "pending",
  "rejected",
]

export const TASK_GROUPINGS = ["task_group", "status"] as const

export type TaskGrouping = (typeof TASK_GROUPINGS)[number]

export const TASK_GROUPING_LABELS: Record<TaskGrouping, string> = {
  task_group: "Task group",
  status: "Status",
}

const STANDALONE_GROUPINGS: readonly TaskGrouping[] = ["status"]

export const taskFilterSchema = z.object({
  archived: z.boolean().optional().catch(false),
  search: z.string().optional().catch(undefined),
  status: z.array(z.enum(ReviewStatusSchema.enum)).optional().catch(undefined),
  group: z
    .enum([...TASK_GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: sortSchema.optional().catch(undefined),
  direction: z.enum(SortDirectionSchema.enum).optional().catch(undefined),
})

export type TaskFilters = z.infer<typeof taskFilterSchema>

export const NO_TASK_FILTERS: Pick<TaskFilters, "status"> = {
  status: undefined,
}

export function hasTaskFilters(filters: TaskFilters): boolean {
  return Boolean(filters.status?.length)
}

export function taskListParams(filters: TaskFilters, packageId: string | null) {
  return {
    deleted: filters.archived ?? false,
    search: filters.search || null,
    package_id: packageId,
    standalone: packageId === null,
    review_status: filters.status?.length ? filters.status : null,
    sort_by: filters.sort ?? "number",
    direction: filters.direction ?? "asc",
  }
}

export function taskColumns(filters: TaskFilters): Column[] {
  return filters.columns ? withFixed(filters.columns) : DEFAULT_COLUMNS
}

export function taskSort(filters: TaskFilters): ListSort<TaskSort> {
  return {
    column: filters.sort ?? "number",
    direction: filters.direction ?? "asc",
  }
}

export function sortSearch(
  filters: TaskFilters,
  column: TaskSort,
): Pick<TaskFilters, "sort" | "direction"> {
  const current = taskSort(filters)
  const direction =
    column === current.column && current.direction === "asc" ? "desc" : "asc"
  return column === "number" && direction === "asc"
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

export function columnsSearch(columns: Column[]): Pick<TaskFilters, "columns"> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

export const ACTIVE_TEMPLATE_QUOTES = { archived: false, limit: 500 }

export function taskGroupings(
  packageId: string | null,
  taskGroupsReadable: boolean,
): readonly TaskGrouping[] {
  return packageId && taskGroupsReadable ? TASK_GROUPINGS : STANDALONE_GROUPINGS
}

function defaultGrouping(
  offered: readonly TaskGrouping[],
): TaskGrouping | undefined {
  return offered.includes("task_group") ? "task_group" : undefined
}

export function taskDisplay(
  filters: TaskFilters,
  offered: readonly TaskGrouping[],
): Display<TaskGrouping> {
  const group =
    filters.group === "none"
      ? undefined
      : filters.group && offered.includes(filters.group)
        ? filters.group
        : defaultGrouping(offered)
  return { group, subgroup: undefined }
}

export function displaySearch(
  next: Display<TaskGrouping>,
  offered: readonly TaskGrouping[],
): Pick<TaskFilters, "group"> {
  const fallback = defaultGrouping(offered)
  return {
    group:
      next.group === fallback
        ? undefined
        : (next.group ?? (fallback && "none")),
  }
}

export function taskGroupParams(
  filters: TaskFilters,
  packageId: string | null,
  group: TaskGrouping,
) {
  return { ...taskListParams(filters, packageId), group }
}

export function reviewStatusLabel(status: ReviewStatus): string {
  switch (status) {
    case "approved":
      return "Approved"
    case "pending":
      return "Draft"
    case "rejected":
      return "Rejected"
  }
}
